import React from 'react';
import { motion } from 'framer-motion';
import { CTAButton } from './CTAButton';
export function FrameworkSection() {
  return <section className="py-20 md:py-28 px-4 bg-warm-gray watermark-bg" data-id="element-104">
      <div className="max-w-5xl mx-auto" data-id="element-105">
        <div className="text-center mb-16" data-id="element-106">
          <span className="text-gold font-semibold tracking-widest uppercase text-sm mb-4 block" data-id="element-107">
            Introducing
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6" data-id="element-108">
            The 3-Filter Discernment Framework
          </h2>
          <p className="text-xl text-charcoal/80 max-w-2xl mx-auto mb-8" data-id="element-109">
            The only relationship decision system built on Body + Fruit + Timing
          </p>
          <p className="text-lg text-charcoal/70 max-w-3xl mx-auto" data-id="element-110">
            This is not a devotional. This is not a self-help book. This is not
            "pray harder and wait."
            <br data-id="element-111" />
            This is a repeatable, 5-minute system that turns any loud
            relationship decision into one of three clear outcomes:
          </p>

          <div className="flex flex-col md:flex-row justify-center gap-4 md:gap-8 mt-8" data-id="element-112">
            <div className="flex items-center gap-3 justify-center" data-id="element-113">
              <div className="w-4 h-4 rounded-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]" data-id="element-114"></div>
              <span className="font-medium" data-id="element-115">
                <strong data-id="element-116">GREEN</strong> — Move forward.
              </span>
            </div>
            <div className="flex items-center gap-3 justify-center" data-id="element-117">
              <div className="w-4 h-4 rounded-full bg-yellow-400 shadow-[0_0_10px_rgba(250,204,21,0.5)]" data-id="element-118"></div>
              <span className="font-medium" data-id="element-119">
                <strong data-id="element-120">YELLOW</strong> — Wait 24–72 hours.
              </span>
            </div>
            <div className="flex items-center gap-3 justify-center" data-id="element-121">
              <div className="w-4 h-4 rounded-full bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]" data-id="element-122"></div>
              <span className="font-medium" data-id="element-123">
                <strong data-id="element-124">RED</strong> — Pause. Do not decide today.
              </span>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8" data-id="element-125">
          {/* Filter 1: Body */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-2xl p-8 shadow-sm border-t-4 border-green-500 relative overflow-hidden" data-id="element-126">
            <div className="absolute top-0 right-0 w-24 h-24 bg-green-500/5 rounded-bl-full -mr-4 -mt-4" data-id="element-127"></div>
            <div className="text-green-600 font-bold tracking-wider uppercase text-sm mb-2" data-id="element-128">
              Filter #1
            </div>
            <h3 className="text-2xl font-bold mb-4" data-id="element-129">
              BODY{' '}
              <span className="text-charcoal/40 text-lg font-normal" data-id="element-130">
                (90 seconds)
              </span>
            </h3>
            <p className="font-serif italic text-lg mb-6 text-charcoal/90 border-l-2 border-green-200 pl-4" data-id="element-131">
              "When I imagine each option — do I feel open or tight?"
            </p>
            <p className="text-charcoal/80 leading-relaxed" data-id="element-132">
              Your body is not infallible. But it is honest. Before any
              decision, your nervous system knows something your mind is still
              arguing about. The Body Check gives you that signal — in 90
              seconds — before pressure and urgency cloud everything.
            </p>
          </motion.div>

          {/* Filter 2: Fruit */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-2xl p-8 shadow-sm border-t-4 border-yellow-400 relative overflow-hidden" data-id="element-133">
            <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-400/5 rounded-bl-full -mr-4 -mt-4" data-id="element-134"></div>
            <div className="text-yellow-600 font-bold tracking-wider uppercase text-sm mb-2" data-id="element-135">
              Filter #2
            </div>
            <h3 className="text-2xl font-bold mb-4" data-id="element-136">
              FRUIT{' '}
              <span className="text-charcoal/40 text-lg font-normal" data-id="element-137">
                (2 minutes)
              </span>
            </h3>
            <p className="font-serif italic text-lg mb-6 text-charcoal/90 border-l-2 border-yellow-200 pl-4" data-id="element-138">
              "In 6 months — will I feel more myself or less?"
            </p>
            <p className="text-charcoal/80 leading-relaxed" data-id="element-139">
              Emotions lie in the moment. Fruit doesn't. The Fruit Projection
              asks you to step out of the urgency of today and look at what this
              relationship path actually produces over time. Peace. Growth.
              Identity. Or depletion, drift, and regret.
            </p>
          </motion.div>

          {/* Filter 3: Timing */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="bg-white rounded-2xl p-8 shadow-sm border-t-4 border-red-500 relative overflow-hidden" data-id="element-140">
            <div className="absolute top-0 right-0 w-24 h-24 bg-red-500/5 rounded-bl-full -mr-4 -mt-4" data-id="element-141"></div>
            <div className="text-red-600 font-bold tracking-wider uppercase text-sm mb-2" data-id="element-142">
              Filter #3
            </div>
            <h3 className="text-2xl font-bold mb-4" data-id="element-143">
              TIMING{' '}
              <span className="text-charcoal/40 text-lg font-normal" data-id="element-144">
                (1 minute)
              </span>
            </h3>
            <p className="font-serif italic text-lg mb-6 text-charcoal/90 border-l-2 border-red-200 pl-4" data-id="element-145">
              "Is this Green, Yellow, or Red right now?"
            </p>
            <p className="text-charcoal/80 leading-relaxed" data-id="element-146">
              Urgency is not the same as timing. Pressure is not the same as
              peace. The Timing Filter asks one question: Is this the right
              moment to act — or am I rushing because I'm afraid of the answer?
            </p>
          </motion.div>
        </div>

        <div className="text-center mt-16 space-y-8" data-id="element-147">
          <p className="text-2xl font-serif italic text-gold font-bold" data-id="element-148">
            "Three filters. One direction. Five minutes."
          </p>
          <CTAButton mainText="GET THE FRAMEWORK — $17.95" subText="Start using the 3 filters tonight." large data-id="element-149" />
        </div>
      </div>
    </section>;
}